/**
 * First-Party Cookie Utilities
 * Implements server-side cookies to bypass ITP (Intelligent Tracking Prevention) limits
 * and extend cookie duration beyond browser restrictions
 */

const COOKIE_API_ENDPOINT = '/api/cookies';

/**
 * Set a cookie via server-side API (bypasses ITP limits)
 * Cookies set server-side can have longer expiration (up to 2 years)
 */
export async function setServerCookie(
  name: string,
  value: string,
  options: {
    maxAge?: number; // in seconds (default: 2 years)
    domain?: string;
    path?: string;
    secure?: boolean;
    sameSite?: 'Strict' | 'Lax' | 'None';
  } = {}
): Promise<boolean> {
  try {
    const { buildApiUrl } = await import('../config/api');
    
    const response = await fetch(buildApiUrl(COOKIE_API_ENDPOINT), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Important: include cookies in request
      body: JSON.stringify({
        name,
        value,
        maxAge: options.maxAge || 63072000, // 2 years in seconds
        domain: options.domain,
        path: options.path || '/',
        secure: options.secure !== false, // Default to true in production
        sameSite: options.sameSite || 'Lax',
      }),
    });

    if (!response.ok) {
      console.warn(`[Cookies] Failed to set server cookie: ${name}`);
      // Fallback to client-side cookie
      setClientCookie(name, value, options);
      return false;
    }

    return true;
  } catch (error) {
    console.error(`[Cookies] Error setting server cookie: ${name}`, error);
    // Fallback to client-side cookie
    setClientCookie(name, value, options);
    return false;
  }
}

/**
 * Get a cookie value (checks server-side first, then client-side)
 */
export async function getCookie(name: string): Promise<string | null> {
  // First try client-side (faster)
  const clientValue = getClientCookie(name);
  if (clientValue) {
    return clientValue;
  }

  // If not found client-side, try server-side
  try {
    const { buildApiUrl } = await import('../config/api');
    
    const response = await fetch(buildApiUrl(`${COOKIE_API_ENDPOINT}/${encodeURIComponent(name)}`), {
      method: 'GET',
      credentials: 'include',
    });

    if (response.ok) {
      const data = await response.json();
      return data.value || null;
    }
  } catch (error) {
    console.error(`[Cookies] Error getting server cookie: ${name}`, error);
  }

  return null;
}

/**
 * Delete a cookie (server-side)
 */
export async function deleteCookie(name: string, options: { domain?: string; path?: string } = {}): Promise<boolean> {
  try {
    const { buildApiUrl } = await import('../config/api');
    
    const response = await fetch(buildApiUrl(`${COOKIE_API_ENDPOINT}/${encodeURIComponent(name)}`), {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        domain: options.domain,
        path: options.path || '/',
      }),
    });

    // Also delete client-side cookie
    deleteClientCookie(name, options);

    return response.ok;
  } catch (error) {
    console.error(`[Cookies] Error deleting server cookie: ${name}`, error);
    deleteClientCookie(name, options);
    return false;
  }
}

/**
 * Client-side cookie fallback (for when server-side fails)
 */
function setClientCookie(
  name: string,
  value: string,
  options: {
    maxAge?: number;
    domain?: string;
    path?: string;
    secure?: boolean;
    sameSite?: 'Strict' | 'Lax' | 'None';
  } = {}
): void {
  if (typeof document === 'undefined') {
    return;
  }

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  if (options.maxAge) {
    cookieString += `; max-age=${options.maxAge}`;
  }

  if (options.domain) {
    cookieString += `; domain=${options.domain}`;
  }

  cookieString += `; path=${options.path || '/'}`;

  if (options.secure !== false) {
    cookieString += '; secure';
  }

  cookieString += `; samesite=${options.sameSite || 'Lax'}`;

  document.cookie = cookieString;
}

/**
 * Get client-side cookie
 */
function getClientCookie(name: string): string | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const nameEQ = encodeURIComponent(name) + '=';
  const cookies = document.cookie.split(';');

  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i];
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1, cookie.length);
    }
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length, cookie.length));
    }
  }

  return null;
}

/**
 * Delete client-side cookie
 */
function deleteClientCookie(name: string, options: { domain?: string; path?: string } = {}): void {
  if (typeof document === 'undefined') {
    return;
  }

  let cookieString = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 UTC`;

  if (options.domain) {
    cookieString += `; domain=${options.domain}`;
  }

  cookieString += `; path=${options.path || '/'}`;

  document.cookie = cookieString;
}

/**
 * Set consent cookie with extended duration (2 years) via server
 */
export async function setConsentCookie(preferences: any): Promise<boolean> {
  return await setServerCookie('cookie_consent', JSON.stringify(preferences), {
    maxAge: 63072000, // 2 years
    path: '/',
    secure: true,
    sameSite: 'Lax',
  });
}






